import React, { useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';
import { theme } from '@noths/polaris-client-ribbons-base';
import { Accordion } from '@noths/polaris-client-ribbons-design-system';
import { pxToRem } from '@noths/polaris-client-styles';

import { FacetHeader } from 'src/components/atoms/FacetHeader/FacetHeader';
import { DESKTOP_FILTER_HEIGHT_CSS_VAR } from 'src/components/organisms/ListingsControls/cssVariables';
import * as styles from 'src/components/organisms/ListingsControls/ListingsControls.styles';
import type {
  ChangedFacetDetails,
  ChangedSortDetails,
} from 'src/components/organisms/ListingsControlsOptions/types';
import type { UIFacet } from 'src/components/types/UIFacet';
import type { UISortOption } from 'src/components/types/UISortOption';
import { FacetOptionsMulti } from '../ListingsControlsOptions/FacetOptionsMulti';
import { FacetOptionsSingle } from '../ListingsControlsOptions/FacetOptionsSingle';
import { SortOptions } from '../ListingsControlsOptions/SortOptions';

export const SORT_ACCORDION_ID = 'sort-accordion';
const SORT_TITLE = 'Sort';

const setFacetHeight = (windowHeight: number) =>
  document.documentElement.style.setProperty(
    DESKTOP_FILTER_HEIGHT_CSS_VAR,
    `calc(${windowHeight}px - ${pxToRem(theme.spacing.xl)})`,
  );

export interface ListingsControlsProps {
  facets: UIFacet[];
  onAllFacetsVisible: VoidFunction;
  onFacetOptionChange: (changedFacet: ChangedFacetDetails) => void;
  onSortOptionChange: (changedSort: ChangedSortDetails) => void;
  sortOptions: UISortOption[];
}

export const ListingsControls = ({
  facets,
  onAllFacetsVisible,
  onFacetOptionChange,
  onSortOptionChange,
  sortOptions,
}: ListingsControlsProps) => {
  const { height } = useWindowSize();
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);

  useEffect(() => {
    setFacetHeight(height);
  }, [height]);

  const handleFacetVisibility = (facetKey: string) => {
    const facetIndex = facets.findIndex(({ key }) => key === facetKey);
    const lastFacetIndex = facetIndex === facets.length - 1;

    if (lastFacetIndex) {
      onAllFacetsVisible();
    }
  };

  return (
    <form css={[styles.filterListWrapper]} onSubmit={(e) => e.preventDefault()}>
      <div css={[styles.list]}>
        <hr css={styles.separator} />
        {sortOptions.length > 0 &&
          (() => {
            const sortOpen = openAccordionId === SORT_ACCORDION_ID;

            return (
              <div css={styles.listItem}>
                <Accordion
                  iconColor="var(--brand-purple-base)"
                  id={SORT_ACCORDION_ID}
                  onClick={(ev) => {
                    ev.preventDefault();
                    setOpenAccordionId(sortOpen ? null : SORT_ACCORDION_ID);
                  }}
                  open={sortOpen}
                  title={
                    <FacetHeader
                      heading={SORT_TITLE}
                      options={!sortOpen ? sortOptions : undefined}
                    />
                  }
                >
                  <div css={styles.facetOptionsAccordionWrapper}>
                    <SortOptions
                      accessibleLabel={SORT_TITLE}
                      onSortOptionChange={onSortOptionChange}
                      sortOptions={sortOptions}
                    />
                  </div>
                </Accordion>
              </div>
            );
          })()}
        {facets.map((facet) => {
          if (facet.options.length === 0) {
            return null;
          }

          const activeOptions = facet.options.filter((option) => option.active);
          const hasActiveOptions = activeOptions.length > 0;
          const facetOpen = openAccordionId === facet.key;
          const FacetOptionsComponent =
            facet.type === 'multi' ? FacetOptionsMulti : FacetOptionsSingle;

          return (
            <React.Fragment key={facet.key}>
              <hr css={styles.separator} />
              <div css={styles.listItem}>
                {facet.options.length > 1 ? (
                  <Accordion
                    iconColor={hasActiveOptions ? 'var(--brand-purple-base)' : 'currentColor'}
                    id={facet.key}
                    onClick={(ev) => {
                      ev.preventDefault();
                      setOpenAccordionId(facetOpen ? null : facet.key);
                    }}
                    onVisible={() => handleFacetVisibility(facet.key)}
                    open={facetOpen}
                    title={
                      <FacetHeader
                        heading={facet.title}
                        options={!facetOpen ? facet.options : undefined}
                      />
                    }
                  >
                    <div css={styles.facetOptionsAccordionWrapper}>
                      <FacetOptionsComponent
                        facet={facet}
                        onFacetOptionChange={onFacetOptionChange}
                      />
                    </div>
                  </Accordion>
                ) : (
                  <div
                    css={styles.facetOptionsWrapper}
                    style={
                      {
                        '--borderColor': hasActiveOptions
                          ? 'var(--brand-purple-base)'
                          : 'transparent',
                      } as styles.BorderCSS
                    }
                  >
                    <FacetOptionsComponent
                      facet={facet}
                      onFacetOptionChange={onFacetOptionChange}
                    />
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
        <hr css={styles.separator} />
      </div>
    </form>
  );
};
